import React from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";
import { DEALER } from "@/data/dealership";
import { useT } from "@/i18n/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useT();
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.required"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(`${t("contact.error")} ${DEALER.phone}`);
    } finally {
      setLoading(false);
    }
  };

  const hoursLabel = (h) => (lang === "es" ? h.dayEs : h.day);
  const hoursVal = (h) => (lang === "es" && h.hoursEs ? h.hoursEs : h.hours);

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 lg:px-16 py-20 md:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#EB0A1E]" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#EB0A1E]">
              {t("contact.eyebrow")}
            </span>
          </div>
          <h2 className="font-display font-bold text-[#0A0A0A] text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            {t("contact.h1a")}<br />{t("contact.h1b")}
          </h2>

          <div className="mt-10 space-y-6">
            <InfoRow icon={<Phone className="w-5 h-5" />} label={t("contact.directLine")}
              value={DEALER.phone} href={`tel:${DEALER.phoneRaw}`} testid="contact-phone" />
            <InfoRow icon={<MapPin className="w-5 h-5" />} label={t("contact.dealership")}
              value={DEALER.address} href={DEALER.mapsUrl} external testid="contact-map" />
            <InfoRow icon={<Clock className="w-5 h-5" />} label={t("contact.hours")}
              value={DEALER.hours.map((h) => `${hoursLabel(h)}: ${hoursVal(h)}`).join(" · ")}
              testid="contact-hours" />
          </div>

          <div className="mt-10 border-t border-[#E4E4E7] pt-6">
            {DEALER.hours.map((h) => (
              <div key={h.day} className="flex justify-between py-2 border-b border-[#E4E4E7] last:border-0">
                <span className="text-sm font-medium text-[#0A0A0A]">{hoursLabel(h)}</span>
                <span className="text-sm text-[#52525B]">{hoursVal(h)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <form onSubmit={submit} data-testid="inquiry-form"
            className="bg-[#F8F8F9] border border-[#E4E4E7] p-6 md:p-10 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("contact.formName")} id="iname">
                <Input id="iname" required data-testid="inq-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-none border-[#E4E4E7] bg-white focus-visible:ring-[#EB0A1E]" />
              </Field>
              <Field label={t("contact.formEmail")} id="iemail">
                <Input id="iemail" type="email" required data-testid="inq-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none border-[#E4E4E7] bg-white focus-visible:ring-[#EB0A1E]" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("contact.formPhone")} id="iphone">
                <Input id="iphone" data-testid="inq-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none border-[#E4E4E7] bg-white focus-visible:ring-[#EB0A1E]" />
              </Field>
              <Field label={t("contact.formSubject")} id="isubject">
                <Input id="isubject" data-testid="inq-subject"
                  placeholder={t("contact.formSubjectPh")}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="rounded-none border-[#E4E4E7] bg-white focus-visible:ring-[#EB0A1E]" />
              </Field>
            </div>
            <Field label={t("contact.formMessage")} id="imessage">
              <Textarea id="imessage" rows={6} required data-testid="inq-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="rounded-none border-[#E4E4E7] bg-white focus-visible:ring-[#EB0A1E] resize-none" />
            </Field>
            <button type="submit" disabled={loading} data-testid="inq-submit"
              className="w-full bg-[#0A0A0A] hover:bg-[#EB0A1E] text-white font-semibold py-4 transition-colors disabled:opacity-50">
              {loading ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value, href, external, testid }) {
  const content = (
    <div className="flex items-start gap-4 group" data-testid={testid}>
      <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center flex-shrink-0 group-hover:bg-[#EB0A1E] transition-colors">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.15em] text-[#52525B] mb-1">{label}</div>
        <div className="font-display font-semibold text-[#0A0A0A] flex items-center gap-1">
          {value}
          {external && <ArrowUpRight className="w-4 h-4 opacity-60" />}
        </div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}>
        {content}
      </a>
    );
  }
  return content;
}
